import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';

interface ExcelViewerProps {
  fileUrl: string;
  zoom: number;
}

interface SheetData {
  name: string;
  data: any[][];
}

const ExcelViewer: React.FC<ExcelViewerProps> = ({ fileUrl, zoom }) => {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadWorkbook = async () => {
      try {
        setLoading(true);
        setError('');

        // Télécharger le fichier
        const response = await fetch(fileUrl);
        const arrayBuffer = await response.arrayBuffer();

        // Lire le fichier Excel
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        // Extraire toutes les feuilles
        const sheetsData: SheetData[] = workbook.SheetNames.map((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          return {
            name: sheetName,
            data: data as any[][],
          };
        });

        setSheets(sheetsData);
      } catch (err) {
        setError('Erreur lors du chargement du fichier Excel');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadWorkbook();
  }, [fileUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  const currentSheet = sheets[activeSheet];

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Onglets des feuilles */}
      {sheets.length > 1 && (
        <div className="flex gap-2 p-2 border-b overflow-x-auto">
          {sheets.map((sheet, index) => (
            <button
              key={index}
              onClick={() => setActiveSheet(index)}
              className={`px-4 py-2 rounded text-sm whitespace-nowrap ${
                activeSheet === index
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}

      {/* Tableau */}
      <div
        className="overflow-auto p-4"
        style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}
      >
        <table className="min-w-full border-collapse border border-gray-300">
          <tbody>
            {currentSheet?.data.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex === 0 ? 'bg-gray-100 font-semibold' : ''}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className="border border-gray-300 px-4 py-2 text-sm"
                  >
                    {cell !== null && cell !== undefined ? String(cell) : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExcelViewer;
